import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import SortableGrid from "@/components/SortableGrid";
import SessionReviews from "@/components/SessionReviews";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useEditMode } from "@/lib/editMode";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, Plus, Target, Award, MapPin, GripVertical, CalendarDays } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip } from "recharts";
import ClinicianVideos from "@/components/ClinicianVideos";
import RTSPanel from "@/components/RTSPanel";
import UniversalPhysicalTestsPanel from "@/components/UniversalPhysicalTestsPanel";
import PatientFormResponses from "@/components/PatientFormResponses";
import ProgrammeVersionHistory from "@/components/ProgrammeVersionHistory";
import ProgrammeChangeHistory from "@/components/ProgrammeChangeHistory";
import ClinicianRoadmapPanel from "@/components/ClinicianRoadmapPanel";
import { RehabStageManager } from "@/components/RehabStages";
import PatientInvestigations from "@/components/PatientInvestigations";
import PatientCases from "@/components/PatientCases";
import GeneratorAssignmentPanel from "@/components/GeneratorAssignmentPanel";
import ProgressConcernEvidenceAI from "@/components/ProgressConcernEvidenceAI";
import BodyMap from "@/components/BodyMap";
import PatientAssessmentNotes from "@/components/PatientAssessmentNotes";
import { Skeleton } from "@/components/Skeleton";

const TABS = [
  ["overview", "Overview"],
  ["cases", "Cases & Programmes"],
  ["tests", "Physical Tests"],
  ["notes", "Assessments & Notes"],
  ["forms", "Forms"],
  ["files", "Investigations & Files"],
  ["appointments", "Appointments"],
  ["contact", "Contact"],
];

function ContactPanel({ patient, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const start = () => {
    setF({
      phone: patient?.phone || "", address_line1: patient?.address_line1 || "",
      address_line2: patient?.address_line2 || "", city: patient?.city || "",
      postcode: patient?.postcode || "", country: patient?.country || "",
    });
    setEdit(true);
  };
  const save = async () => {
    if (!window.confirm("Save these contact changes? The update is recorded in the clinical audit trail.")) return;
    setBusy(true);
    try {
      await api.patch(`/clinician/patients/${patient.id}/contact`, f);
      toast.success("Contact details updated");
      setEdit(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update contact details");
    } finally { setBusy(false); }
  };
  const field = (key, label) => (
    <div>
      <div className="fl-label mb-1 text-xs">{label}</div>
      <input className="fl-input w-full text-sm" value={f[key] || ""} onChange={(e) => setF({ ...f, [key]: e.target.value })} data-testid={`pt-contact-${key}`} />
    </div>
  );
  return (
    <div className="fl-card fl-card-elevated p-6" data-testid="pt-contact-card">
      <div className="flex items-center justify-between mb-4">
        <div className="fl-heading text-xl">Contact details</div>
        {!edit && <button className="fl-btn-ghost text-xs" onClick={start} data-testid="pt-contact-edit">Edit contact details</button>}
      </div>
      {!edit ? (
        (patient?.phone || patient?.address_line1 || patient?.city || patient?.postcode) ? (
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div><span className="text-zinc-500">Phone:</span> <span className="text-white" data-testid="pt-contact-phone">{patient?.phone || "—"}</span></div>
            <div><span className="text-zinc-500">Email:</span> <span className="text-white">{patient?.email}</span></div>
            <div className="sm:col-span-2">
              <span className="text-zinc-500">Address:</span>{" "}
              <span className="text-white" data-testid="pt-contact-address">
                {[patient?.address_line1, patient?.address_line2, patient?.city, patient?.postcode, patient?.country].filter(Boolean).join(", ") || "—"}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-zinc-500 text-sm" data-testid="pt-contact-empty">
            No contact details yet. You can add them here, or the patient can add their own from Account → Security &amp; privacy.
          </div>
        )
      ) : (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            {field("phone", "Phone")}
            {field("address_line1", "Address line 1")}
            {field("address_line2", "Address line 2")}
            {field("city", "City / town")}
            {field("postcode", "Postcode")}
            {field("country", "Country")}
          </div>
          <div className="text-[11px] text-zinc-500">Email and login are not editable here. Changes are audited.</div>
          <div className="flex gap-2">
            <button className="fl-btn text-sm" disabled={busy} onClick={save} data-testid="pt-contact-save">{busy ? "Saving…" : "Save changes"}</button>
            <button className="fl-btn-ghost text-sm" disabled={busy} onClick={() => setEdit(false)} data-testid="pt-contact-cancel">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ClinicianPatient() {
  const { id } = useParams();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const setTab = (t) => setSp((prev) => { const n = new URLSearchParams(prev); n.set("tab", t); return n; });

  const [p, setP] = useState(null);
  const [clinicianOpts, setClinicianOpts] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState("");
  const [prog, setProg] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [aiOut, setAiOut] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [library, setLibrary] = useState([]);
  const [appts, setAppts] = useState(null);
  // iter100 · goal countdown, reward tier, aggregate pain heatmap.
  const [ptGoal, setPtGoal] = useState(null);
  const [ptRewards, setPtRewards] = useState(null);
  const [ptPain, setPtPain] = useState({ regions: [], days_covered: 0 });
  // iter101 · patient's latest pain-map markers (interactive body-map view).
  const [ptPainMap, setPtPainMap] = useState({ pain_locations: [], created_at: null });
  // iter101.5 · rearrangeable clinician-strip widget layout
  const { editing: editLayout } = useEditMode();
  const {
    visibleOrder: stripOrder, setOrder: setStripOrder,
    toggleHidden: hideStrip, resetToDefault: resetStrip,
    setSize: sizeStrip, sizes: stripSizes,
    isDirty: stripDirty,
  } = useDashboardLayout("clinician_patient_strip", ["goal", "rewards", "pain_heatmap"]);

  const refresh = async () => {
    const [pt, pr, ci, lg, nt, lib, g, r, ph, pm] = await Promise.all([
      api.get(`/patients/${id}`), api.get(`/programmes/patient/${id}`),
      api.get(`/checkins/patient/${id}`), api.get(`/exercise-logs/patient/${id}`),
      api.get(`/notes/patient/${id}`), api.get(`/exercises`),
      api.get(`/patients/${id}/goal`).catch(() => ({ data: { goal: null } })),
      api.get(`/patients/${id}/rewards`).catch(() => ({ data: null })),
      api.get(`/patients/${id}/pain-history`).catch(() => ({ data: { regions: [], days_covered: 0 } })),
      api.get(`/patients/${id}/pain-map`).catch(() => ({ data: { pain_locations: [], created_at: null } })),
    ]);
    setP(pt.data); setProg(pr.data); setCheckins(ci.data); setLogs(lg.data);
    setNotes(nt.data);
    setLibrary(Array.isArray(lib.data) ? lib.data : (lib.data?.items || []));
    setPtGoal(g.data?.goal || null);
    setPtRewards(r.data || null);
    setPtPain(ph.data || { regions: [], days_covered: 0 });
    setPtPainMap(pm.data || { pain_locations: [], created_at: null });
  };
  useEffect(() => { refresh(); }, [id]);
  useEffect(() => {
    if (user?.is_admin) api.get("/public/clinicians").then((r) => setClinicianOpts(r.data || [])).catch(() => {});
  }, [user]);
  // Appointments are lazy-loaded the first time the tab is opened (reuses the
  // clinician's appointment feed, filtered to this patient).
  useEffect(() => {
    if (tab !== "appointments" || appts !== null || !user?.id) return;
    api.get(`/appointments/by-clinician/${user.id}`)
      .then((r) => setAppts((r.data || []).filter((a) => a.patient_id === id)))
      .catch(() => setAppts([]));
  }, [tab, appts, user, id]);

  const assignedName = (cid) => clinicianOpts.find((c) => c.id === cid)?.name || "Unassigned";
  const doReassign = async () => {
    try {
      await api.patch(`/admin/users/${id}`, { assigned_clinician_id: reassignTo || null });
      toast.success("Patient reassigned");
      setReassignOpen(false);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reassign failed"); }
  };

  const generateSummary = async () => {
    setAiOut(""); setAiLoading(true);
    try {
      const token = localStorage.getItem("fl_token");
      const res = await fetch(`${API}/ai/patient-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ patient_id: id }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setAiOut((s) => s + dec.decode(value));
      }
    } catch (e) { toast.error("AI generation failed"); }
    finally { setAiLoading(false); }
  };

  const chartData = [...checkins].reverse().slice(-14).map((c) => ({
    date: new Date(c.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
    pain: c.pain, wellness: c.wellness,
  }));

  return (
    <DashLayout>
      {/* ---------------- Persistent patient header ---------------- */}
      <div className="mb-4" data-testid="patient-workspace-header">
        <Link to="/clinician" className="text-xs text-zinc-500 hover:text-white">← Back to patients</Link>
        <div className="flex items-start justify-between gap-4 mt-2 flex-wrap">
          <div>
            <div className="fl-label text-[#f47a20]">Patient profile</div>
            <h1 className="fl-heading text-3xl mt-1" data-testid="patient-name">{p?.name}</h1>
            <div className="text-zinc-400 text-sm mt-1">{p?.sport && `${p.sport} · `}{p?.main_goal}</div>
            {user?.is_admin && (
              <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="reassign-control">
                <span className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
                  Assigned clinician: <b className="text-white">{assignedName(p?.assigned_clinician_id)}</b>
                </span>
                {!reassignOpen ? (
                  <button className="fl-btn-ghost text-[11px]" onClick={() => { setReassignTo(p?.assigned_clinician_id || ""); setReassignOpen(true); }} data-testid="reassign-open">Change</button>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <select className="fl-input text-xs py-1" value={reassignTo} onChange={(e) => setReassignTo(e.target.value)} data-testid="reassign-select">
                      <option value="">— Unassigned —</option>
                      {clinicianOpts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button className="fl-btn text-[11px]" onClick={doReassign} data-testid="reassign-save">Reassign</button>
                    <button className="fl-btn-ghost text-[11px]" onClick={() => setReassignOpen(false)}>Cancel</button>
                    <div className="w-full text-[11px] mt-1" style={{ color: "var(--fl-muted-2)" }} data-testid="reassign-confirm-text">
                      Reassign from {assignedName(p?.assigned_clinician_id)} to {assignedName(reassignTo)}? Existing appointments and historical clinical records will not be changed.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <button onClick={generateSummary} disabled={aiLoading} className="fl-btn text-sm" data-testid="ai-btn">
              <Sparkles size={14} /> {aiLoading ? "Generating..." : "AI progress summary"}
            </button>
          </div>
        </div>
      </div>

      {/* ---------------- Patient-profile sub navigation ---------------- */}
      <div className="mb-6 overflow-x-auto no-scrollbar" data-testid="patient-tabs">
        <div className="flex gap-1 min-w-max border-b border-white/10">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`pt-tab-${key}`}
              aria-current={tab === key ? "page" : undefined}
              className={`whitespace-nowrap px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${
                tab === key ? "border-[#f47a20] text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ================= OVERVIEW ================= */}
      {tab === "overview" && (
        <div className="space-y-6" data-testid="pt-panel-overview">
          <ProgressConcernEvidenceAI patientId={id} />
          {aiOut && (
            <div className="fl-card fl-card-elevated p-6 border-[#f47a20]/40" data-testid="ai-output">
              <div className="fl-label text-[#f47a20] mb-2">AI-assisted summary · Claude Sonnet 4.5</div>
              <div className="text-zinc-200 text-sm whitespace-pre-wrap leading-relaxed">{aiOut}</div>
              <div className="text-[10px] text-zinc-500 mt-3">Generated for clinician review. Requires clinical judgement.</div>
            </div>
          )}

          {(p === null || chartData.length > 0) && (
          <div className="fl-card fl-card-elevated p-6">
            <div className="fl-heading text-xl mb-3">Trends</div>
            {p === null ? (
              <Skeleton className="h-[200px] w-full rounded-lg" data-testid="patient-trends-loading" />
            ) : chartData.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
                  <YAxis stroke="#71717A" fontSize={11} domain={[0, 10]} />
                  <Tooltip contentStyle={{ background: "#141414", border: "1px solid #333" }} />
                  <Line dataKey="pain" stroke="#EF4444" strokeWidth={2} dot={false} />
                  <Line dataKey="wellness" stroke="#22C55E" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : null}
          </div>
          )}

          <div>
            <div className="flex items-center justify-end gap-2 mb-2">
              {editLayout && stripDirty && (
                <button type="button" onClick={resetStrip}
                        className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-md border border-white/10"
                        data-testid="clinician-patient-layout-reset">Reset</button>
              )}
              <button type="button" onClick={() => {}} className="hidden" data-testid="clinician-patient-edit-toggle">
                <GripVertical size={12} /> {editLayout ? "Done" : "Edit strip"}
              </button>
            </div>
            <SortableGrid
              items={stripOrder}
              onReorder={setStripOrder}
              onHide={hideStrip}
              onResize={sizeStrip}
              sizes={stripSizes}
              editMode={editLayout}
              className="grid md:grid-cols-3 gap-4"
            >
              {stripOrder.map((wid) => {
                if (wid === "goal") return (
                  <SortableGrid.Item key="goal" id="goal">
                    <div className="fl-card fl-card-elevated p-5 h-full" data-testid="pt-goal-card">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="text-[#f47a20]" size={16} />
                        <div className="fl-label text-xs">Patient goal</div>
                      </div>
                      {ptGoal ? (
                        <>
                          <div className="fl-heading text-lg leading-tight">{ptGoal.name}</div>
                          {(() => {
                            const d = new Date(ptGoal.target_date + "T00:00:00");
                            const diff = Math.ceil((d.getTime() - Date.now()) / 86400000);
                            return (
                              <>
                                <div className="mt-2 flex items-baseline gap-2">
                                  <span className="fl-heading text-3xl text-[#f47a20] leading-none">
                                    {diff > 0 ? diff : diff === 0 ? "0" : "!"}
                                  </span>
                                  <span className="text-[11px] text-zinc-400 uppercase tracking-widest">
                                    {diff > 0 ? "days remaining" : diff === 0 ? "today" : "past target"}
                                  </span>
                                </div>
                                <div className="text-[11px] text-zinc-500 mt-1">Target · {d.toLocaleDateString("en-GB")}</div>
                              </>
                            );
                          })()}
                        </>
                      ) : <div className="text-zinc-500 text-sm">Patient hasn't set a goal yet.</div>}
                    </div>
                  </SortableGrid.Item>
                );
                if (wid === "rewards") return null;
                if (wid === "pain_heatmap") return (
                  <SortableGrid.Item key="pain_heatmap" id="pain_heatmap">
                    <div className="fl-card fl-card-elevated p-5 h-full" data-testid="pt-pain-heatmap-card">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="text-[#f47a20]" size={16} />
                        <div className="fl-label text-xs">Pain heatmap ({ptPain.days_covered || 0} logs)</div>
                      </div>
                      {(ptPain.regions || []).length === 0 ? (
                        <div className="text-zinc-500 text-sm">No visual pain logs yet.</div>
                      ) : (
                        <div className="space-y-1.5 max-h-40 overflow-auto pr-1" data-testid="pt-pain-list">
                          {[...ptPain.regions].sort((a, b) => (b.avg_pain || 0) - (a.avg_pain || 0)).slice(0, 8).map((r) => {
                            const intensity = Math.min(100, Math.round(((r.avg_pain || 0) / 10) * 100));
                            const color = r.avg_pain >= 7 ? "#EF4444" : r.avg_pain >= 4 ? "#F59E0B" : "#22C55E";
                            return (
                              <div key={r.region_id} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                                <div className="text-xs text-white flex-1 truncate">{r.region}</div>
                                <div className="w-16 h-1.5 bg-white/10 rounded overflow-hidden">
                                  <div className="h-full" style={{ width: `${intensity}%`, background: color }} />
                                </div>
                                <div className="text-[10px] text-zinc-400 w-8 text-right">{r.avg_pain}/10</div>
                                <div className="text-[10px] text-zinc-500 w-6 text-right">×{r.count}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </SortableGrid.Item>
                );
                return null;
              })}
            </SortableGrid>
          </div>

          <div className="fl-card fl-card-elevated p-6">
            <div className="fl-heading text-xl mb-3">Recent exercise logs</div>
            {logs.length === 0 && <div className="text-zinc-500 text-sm">No logs.</div>}
            <div className="grid md:grid-cols-3 gap-3">
              {logs.slice(0, 9).map((l) => (
                <div key={l.id} className="text-sm border border-white/5 rounded p-3">
                  <div className="text-xs text-zinc-500">{new Date(l.created_at).toLocaleDateString("en-GB")}</div>
                  <div className="text-white mt-1">{l.sets_done}×{l.reps_done} · RPE {l.rpe}</div>
                  <div className="text-xs text-zinc-500">Pain {l.pain_before} → {l.pain_after}</div>
                </div>
              ))}
            </div>
          </div>

          <ClinicianRoadmapPanel patientId={id} programme={prog} />
          <RehabStageManager patientId={id} />
          <ClinicianVideos patientId={id} />
        </div>
      )}

      {/* ================= PHYSICAL TESTS ================= */}
      {tab === "tests" && (
        <div className="space-y-6" data-testid="pt-panel-tests">
          <RTSPanel patientId={id} />
          <UniversalPhysicalTestsPanel patientId={id} />
        </div>
      )}

      {/* ================= CASES & PROGRAMMES ================= */}
      {tab === "cases" && (
        <div data-testid="pt-panel-cases">
          <PatientCases patientId={id} patientName={p?.name} library={library} onChanged={refresh} initialCaseId={sp.get("case") || null} />
          <GeneratorAssignmentPanel patientId={id} />
          <details className="fl-card fl-card-elevated p-4 mt-4" data-testid="pt-programme-history">
            <summary className="cursor-pointer fl-label text-xs select-none">Programme history — versions &amp; changes</summary>
            <div className="mt-4 space-y-4">
              <ProgrammeVersionHistory patientId={id} />
              <ProgrammeChangeHistory patientId={id} />
            </div>
          </details>
        </div>
      )}

      {/* ===== PERIODISATION — moved to Programmes → Periodisation. Old links redirect here. ===== */}
      {tab === "periodisation" && (
        <div data-testid="pt-panel-periodisation" className="fl-card p-8 text-center">
          <div className="fl-heading text-lg">Periodisation has moved</div>
          <p className="text-sm text-zinc-400 mt-2">The planner now lives under <b>Programmes → Periodisation</b>, scoped to the programme you open.</p>
          <button
            onClick={() => { window.location.href = `/clinician/periodisation?patient=${id}`; }}
            className="fl-btn text-sm mt-4"
            data-testid="pt-open-periodisation"
          >
            Open Periodisation for this patient →
          </button>
        </div>
      )}

      {/* ================= CLINICAL NOTES ================= */}
      {tab === "notes" && (
        <div className="space-y-4" data-testid="pt-panel-notes">
          <PatientAssessmentNotes patientId={id} legacyNotes={notes} />
        </div>
      )}

      {/* ================= FORMS ================= */}
      {tab === "forms" && (
        <div data-testid="pt-panel-forms">
          <PatientFormResponses patientId={id} />
        </div>
      )}

      {/* ================= INVESTIGATIONS & FILES ================= */}
      {tab === "files" && (
        <div data-testid="pt-panel-files">
          <PatientInvestigations patientId={id} patientName={p?.name} />
        </div>
      )}

      {/* ================= APPOINTMENTS ================= */}
      {tab === "appointments" && (
        <div className="fl-card fl-card-elevated p-6" data-testid="pt-panel-appointments">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="text-[#f47a20]" size={18} />
            <div className="fl-heading text-xl">Appointments</div>
          </div>
          {appts === null ? (
            <div className="text-zinc-500 text-sm">Loading appointments…</div>
          ) : appts.length === 0 ? (
            <div className="text-zinc-500 text-sm" data-testid="pt-appts-empty">
              No appointments with this patient on your calendar.{" "}
              <Link to="/clinician/calendar" className="text-[#f47a20] hover:underline">Open the calendar →</Link>
            </div>
          ) : (
            <div className="divide-y divide-white/5" data-testid="pt-appts-list">
              {appts.map((a) => (
                <div key={a.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                  <div>
                    <div className="text-white">{a.type || a.title || "Appointment"}</div>
                    <div className="text-xs text-zinc-500">
                      {a.start ? new Date(a.start).toLocaleString("en-GB") : (a.date || "—")}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/10 text-zinc-300">
                    {a.status || "scheduled"}
                  </span>
                </div>
              ))}
            </div>
          )}
          <SessionReviews patientId={id} />
        </div>
      )}

      {/* ================= CONTACT ================= */}
      {tab === "contact" && (
        <ContactPanel patient={p} onSaved={refresh} />
      )}
    </DashLayout>
  );
}
